import { useEffect, useState, useMemo } from "react";
import {
  getServices,
  getCustomPricing,
  saveCustomPricingBatch,
  getProperty,
  pricesLoadedFor,
  setSelectedPricing,
  setHasUnsavedChanges,
} from "./custom-pricing.slice";
import { useAppDispatch, useAppSelector } from "@/lib/store";
import { Service } from "@/models/services.model";
import { ServiceWithCustomPricing } from "./custom-pricing.entity";
import { uuid } from "@/interfaces";
import { toast } from "sonner";
import { fieldActionMessage } from "@/lib/appointment-field-actions";
import { changesSavedPrices, pricingFormOf, propertyPricesToSave, type PricingFormEntry } from "./custom-pricing.prices";

function groupByParent(services: ServiceWithCustomPricing[]) {
  const map = new Map();
  for (const service of services) {
    map.set(service.id, { ...service, children: [] });
  }
  const grouped = [];
  for (const service of services) {
    if (service.parent) {
      const parent = map.get(service.parent);
      if (parent) {
        parent.children.push(map.get(service.id));
      }
    } else {
      grouped.push(map.get(service.id));
    }
  }

  return grouped;
}

export function useCustomPricing(propertyId?: uuid) {
  const dispatch = useAppDispatch();
  const {
    list,
    customPricing,
    property,
    loading,
    customPricingLoading,
    propertyLoading,
    propertyNotFound,
    total,
    selectedPricing,
    hasUnsavedChanges,
    customPricingFailed,
  } = useAppSelector((state) => state.customPricing);
  const pricesLoaded = useAppSelector((state) => pricesLoadedFor(state.customPricing, propertyId));

  const [openedServices, setOpenedServices] = useState([] as string[]);
  const [formData, setFormData] = useState<Record<string, PricingFormEntry>>({});

  const vendorId = useAppSelector((state) => state.organization.activeOrgId);

  function handleToggleService(service: string) {
    if (openedServices.includes(service)) {
      setOpenedServices(openedServices.filter((s) => s !== service));
    } else {
      setOpenedServices([...openedServices, service]);
    }
  }

  function loadServices() {
    dispatch(getServices());
  }

  function loadCustomPricing() {
    if (propertyId && vendorId) {
      dispatch(getCustomPricing({ propertyId, vendorId }));
    }
  }

  function loadProperty() {
    if (propertyId) {
      dispatch(getProperty(propertyId));
    }
  }

  useEffect(() => {
    loadServices();
  }, []);

  useEffect(() => {
    loadCustomPricing();
  }, [propertyId, vendorId]);

  useEffect(() => {
    loadProperty();
  }, [propertyId]);

  // Merge services with custom pricing data - memoized to prevent infinite loops
  const servicesWithCustomPricing: ServiceWithCustomPricing[] = useMemo(() => {
    return list.map((service) => {
      const customPricingData = customPricing.find((cp) => cp.service === service.id);
      return {
        ...service,
        customPricing: customPricingData,
        isCustomPricing: !!customPricingData,
      };
    });
  }, [list, customPricing]);

  useEffect(() => {
    const initOpened = servicesWithCustomPricing.length > 0 ? groupByParent(servicesWithCustomPricing)[0].id : "";
    setOpenedServices([initOpened]);
  }, [list.length]); // Use list.length instead of servicesWithCustomPricing.length

  useEffect(() => {
    setFormData(pricesLoaded ? pricingFormOf(list, customPricing) : {});
  }, [pricesLoaded, list, customPricing]);

  function handleSelectPricing(id: string) {
    dispatch(setSelectedPricing(id));
  }

  function handleFormChange(serviceId: string, field: "price" | "payment" | "isDefault", value: number | boolean) {
    setFormData((prev) => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        [field]: value,
      },
    }));
    dispatch(setHasUnsavedChanges(true));
  }

  async function handleSave() {
    if (!propertyId || !vendorId) {
      toast.error("Missing property or vendor information");
      return;
    }
    if (!pricesLoaded) {
      toast.error("This property's prices have not loaded, so there is nothing safe to save");
      return;
    }

    const prices = propertyPricesToSave(list, formData, customPricing);
    if (!changesSavedPrices(prices, customPricing)) {
      toast.info("No changes to save");
      return;
    }

    try {
      await dispatch(saveCustomPricingBatch({ propertyId, vendorId, prices })).unwrap();
      toast.success("Custom pricing saved successfully");
    } catch (error) {
      toast.error(fieldActionMessage(error, "Failed to save custom pricing"));
      console.error("Save error:", error);
    }
  }

  return {
    list: groupByParent(servicesWithCustomPricing),
    property,
    propertyNotFound,
    loading: loading || customPricingLoading || propertyLoading,
    total,
    handleToggleService,
    openedServices,
    handleSelectPricing,
    selectedPricing,
    formData,
    handleFormChange,
    handleSave,
    hasUnsavedChanges,
    pricesLoaded,
    pricesFailed: customPricingFailed,
  };
}
